/**
 * BCOUNT — count every INSERT per block in the DrawingDocument.
 *
 * AutoCAD's BCOUNT prints the number of INSERT entities that reference
 * each block definition, including MINSERT grid multiplicity. Useful
 * for QA passes ("How many doors do we have?") and for debugging
 * PURGE decisions.
 *
 * Pure: the DrawingDocument argument is never mutated.
 *
 * Distinctions captured in the output:
 *   - `insertCount`    — raw number of INSERT entities (each MINSERT is 1)
 *   - `totalCopies`    — INSERT count × (columnCount × rowCount) so a
 *                        3×2 MINSERT contributes 6 copies while still being
 *                        a single INSERT entity
 *   - `isAnonymous`    — block name starts with `*` (excluding layout
 *                        blocks, which are tagged separately)
 *   - `isLayout`       — `*Model_Space` or `*Paper_Space…`
 *
 * The UI displays this as a sortable toast / dialog and optionally
 * filters out anonymous / layout blocks.
 */

import type { DrawingDocument } from "@dxf-ai-editor/drawing-model";

export interface BlockCountEntry {
  readonly blockName: string;
  readonly insertCount: number;
  readonly totalCopies: number;
  readonly isAnonymous: boolean;
  readonly isLayout: boolean;
}

export interface BlockCountInput {
  readonly drawingDocument: DrawingDocument;
  /**
   * If `false` (default), layout blocks (`*Model_Space`, `*Paper_Space…`)
   * are excluded from the result. They always have a single implicit
   * insert whose count is uninteresting.
   */
  readonly includeLayoutBlocks?: boolean;
  /**
   * If `false` (default) anonymous `*Uxxx` / `*Dxxx` / `*Xxxx` blocks
   * are excluded. When the user runs BCOUNT via command line they
   * usually want named blocks only.
   */
  readonly includeAnonymous?: boolean;
}

export function countBlockInstances(
  input: BlockCountInput,
): BlockCountEntry[] {
  const {
    drawingDocument,
    includeLayoutBlocks = false,
    includeAnonymous = false,
  } = input;

  const counts = new Map<
    string,
    { insertCount: number; totalCopies: number }
  >();

  for (const space of drawingDocument.spaces) {
    for (const entity of space.entities) {
      if (entity.type !== "Block") continue;
      const blockName = entity.blockName;
      const cols = Math.max(1, Math.floor(entity.columnCount));
      const rows = Math.max(1, Math.floor(entity.rowCount));
      const copies = cols * rows;
      const current = counts.get(blockName) ?? {
        insertCount: 0,
        totalCopies: 0,
      };
      current.insertCount += 1;
      current.totalCopies += copies;
      counts.set(blockName, current);
    }
  }

  const entries: BlockCountEntry[] = [];
  for (const [blockName, stats] of counts.entries()) {
    const isLayout = isLayoutBlockName(blockName);
    const isAnonymous = !isLayout && blockName.startsWith("*");
    if (isLayout && !includeLayoutBlocks) continue;
    if (isAnonymous && !includeAnonymous) continue;
    entries.push({
      blockName,
      insertCount: stats.insertCount,
      totalCopies: stats.totalCopies,
      isAnonymous,
      isLayout,
    });
  }

  // Sort descending by insertCount, then alphabetically for stable output.
  entries.sort((a, b) => {
    if (b.insertCount !== a.insertCount) return b.insertCount - a.insertCount;
    return a.blockName.localeCompare(b.blockName);
  });

  return entries;
}

function isLayoutBlockName(name: string): boolean {
  if (name === "*Model_Space") return true;
  if (name.startsWith("*Paper_Space")) return true;
  return false;
}

/**
 * Format the result for the command feed — AutoCAD-style summary line
 * per block. Example:
 *
 *     Block instance count:
 *       "Panel": 12 inserts (18 total copies)
 *       "Door": 4 inserts
 *       "*U1": 3 inserts (anonymous)
 */
export function formatBlockCountSummary(
  entries: readonly BlockCountEntry[],
): string {
  if (entries.length === 0) {
    return "No block inserts found.";
  }
  const lines: string[] = ["Block instance count:"];
  for (const entry of entries) {
    const parts = [
      `  "${entry.blockName}": ${entry.insertCount} insert${entry.insertCount === 1 ? "" : "s"}`,
    ];
    if (entry.totalCopies !== entry.insertCount) {
      parts.push(
        ` (${entry.totalCopies} total copies including MINSERT)`,
      );
    }
    if (entry.isAnonymous) parts.push(" (anonymous)");
    if (entry.isLayout) parts.push(" (layout)");
    lines.push(parts.join(""));
  }
  return lines.join("\n");
}
